// Pure geometry for the equipment alert-zone shape (ADDENDUM D1 of
// docs/equipment-tracker-spec.md). A capsule is a stadium shape: two
// parallel straight rails joined by semicircular caps, i.e. the buffer of
// radius `radius` around the segment p1-p2. Emitting it as a single closed
// <path> (rather than a stroked line) gives the shape a genuine boundary,
// so it can be filled AND stroked with the exact same two props 防護區
// uses on its <circle> -- see EquipmentLayer.jsx for that pairing.

// Below this length the segment is treated as a single point and the
// capsule degenerates to a circle -- dividing by `length` below that would
// blow up the unit vector (and, at exactly zero, divide by zero).
const MIN_SEGMENT_LENGTH_PX = 1e-6;

// Full-circle path via two semicircle arcs, used both as the capsule's
// degenerate case (coincident head/tail) and its normal case's caps.
const buildCirclePath = ({ x, y }, radius) =>
  [
    `M ${x - radius} ${y}`,
    `A ${radius} ${radius} 0 1 0 ${x + radius} ${y}`,
    `A ${radius} ${radius} 0 1 0 ${x - radius} ${y}`,
    "Z",
  ].join(" ");

// Builds the SVG path `d` string for the capsule from p1 to p2 with the
// given radius (all in the same pixel space the caller already projected
// into -- this function does no unit conversion).
export const buildCapsulePath = (p1, p2, radius) => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const length = Math.hypot(dx, dy);

  // A one-tag machine (or a two-tag one whose head and tail happen to
  // coincide) has no segment to buffer -- the honest zone shape is a
  // plain circle around the single point.
  if (length < MIN_SEGMENT_LENGTH_PX) {
    return buildCirclePath(p1, radius);
  }

  const ux = dx / length;
  const uy = dy / length;
  // Left-hand normal to the segment (unit vector rotated 90deg), scaled to
  // the buffer radius: the two rails run parallel to the segment, offset
  // by +-n from each of its endpoints.
  const nx = -uy * radius;
  const ny = ux * radius;

  const p1a = { x: p1.x + nx, y: p1.y + ny };
  const p2a = { x: p2.x + nx, y: p2.y + ny };
  const p2b = { x: p2.x - nx, y: p2.y - ny };
  const p1b = { x: p1.x - nx, y: p1.y - ny };

  // Both end caps are semicircles with large-arc-flag 0 and sweep-flag 0.
  // Derivation (see ADDENDUM D1): for p1=(0,0), p2=(10,0), n=(0,r), the
  // first arc runs from p2a (+90deg about p2) to p2b (-90deg about p2) --
  // a DECREASING angle, which is SVG's negative (sweep-flag 0) direction.
  // By the segment's point symmetry the second arc (p1b -> p1a) is the
  // same. Do not "fix" this to sweep-flag 1: that flips both caps to the
  // OUTSIDE of the capsule, self-intersecting the path.
  return [
    `M ${p1a.x} ${p1a.y}`,
    `L ${p2a.x} ${p2a.y}`,
    `A ${radius} ${radius} 0 0 0 ${p2b.x} ${p2b.y}`,
    `L ${p1b.x} ${p1b.y}`,
    `A ${radius} ${radius} 0 0 0 ${p1a.x} ${p1a.y}`,
    "Z",
  ].join(" ");
};
